import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  Router as RouterIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  BugReport as BugReportIcon,
  CheckCircle as CheckCircleIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  NetworkCheck as NetworkCheckIcon,
  Code as CodeIcon,
  Folder as FolderIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';

const CodeBlock = ({ children }) => (
  <Paper
    sx={{
      p: 2,
      bgcolor: '#1e1e1e',
      color: '#d4d4d4',
      fontFamily: 'monospace',
      fontSize: '0.85rem',
      overflow: 'auto',
      borderRadius: 1,
      my: 1
    }}
  >
    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {children}
    </pre>
  </Paper>
);

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const GatewayGuide = () => {
  const [tabValue, setTabValue] = useState(0);
  const [expanded, setExpanded] = useState('panel1');

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        <MenuBookIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Guida Configurazione Gateway OPC UA
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Guida completa per configurare un PC come Gateway OPC UA nel sistema Industria 4.0
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(e, newValue) => setTabValue(newValue)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Architettura" icon={<RouterIcon />} iconPosition="start" />
        <Tab label="Installazione" icon={<BuildIcon />} iconPosition="start" />
        <Tab label="Configurazione" icon={<SettingsIcon />} iconPosition="start" />
        <Tab label="Sicurezza" icon={<SecurityIcon />} iconPosition="start" />
        <Tab label="Troubleshooting" icon={<BugReportIcon />} iconPosition="start" />
      </Tabs>

      {/* TAB 0: Architettura */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <RouterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Modalita di Funzionamento
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                  Il sistema supporta due modalita di connessione OPC UA
                </Alert>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
                  Modalita 1: Backend come Client OPC UA (Diretta)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Il backend si connette direttamente alle macchine:
                </Typography>
                <CodeBlock>
{`Frontend (React) --> Backend (Express) --> Server OPC UA (Macchina)
                      Client OPC UA`}
                </CodeBlock>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
                  Modalita 2: Edge Gateway (Gateway Dedicato)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Un PC dedicato fa da ponte tra macchine e cloud:
                </Typography>
                <CodeBlock>
{`Cloud/Backend <--> Edge Gateway <--> Macchine OPC UA
    (API)          (PC locale)       (produzione)`}
                </CodeBlock>

                <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
                  Vantaggi dell'Edge Gateway:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Funziona anche offline (cache locale)" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Riduce latenza verso le macchine" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Sincronizzazione batch con il cloud" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Isolamento rete di produzione" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Requisiti Hardware
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Componente</TableCell>
                        <TableCell>Minimo</TableCell>
                        <TableCell>Consigliato</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>CPU</TableCell>
                        <TableCell>ARM Cortex-A53</TableCell>
                        <TableCell>ARM Cortex-A72 / x86-64</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>RAM</TableCell>
                        <TableCell>2 GB</TableCell>
                        <TableCell>4 GB</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Storage</TableCell>
                        <TableCell>16 GB</TableCell>
                        <TableCell>32 GB SSD</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Rete</TableCell>
                        <TableCell>100 Mbps</TableCell>
                        <TableCell>1 Gbps</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Dispositivi Supportati
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Raspberry Pi 4/5" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="PC industriale x86" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                    <ListItemText primary="Mini PC (Intel NUC, etc.)" />
                  </ListItem>
                </List>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Software richiesto:
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip label="Linux" size="small" sx={{ mr: 0.5 }} />
                  <Chip label="Node.js 18+" size="small" sx={{ mr: 0.5 }} />
                  <Chip label="PostgreSQL 14+" size="small" sx={{ mr: 0.5 }} />
                  <Chip label="Docker" size="small" variant="outlined" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* TAB 1: Installazione */}
      <TabPanel value={tabValue} index={1}>
        <Accordion expanded={expanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="1" color="primary" size="small" sx={{ mr: 1 }} />
              Installazione con Docker (Consigliata)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock>
{`# Installare Docker
curl -fsSL https://get.docker.com | sh
sudo usermod -aG docker $USER

# Clonare il progetto
git clone <repository-url>
cd industria4/edge-gateway

# Avviare
docker-compose up -d`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'panel2'} onChange={handleAccordionChange('panel2')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="2" color="primary" size="small" sx={{ mr: 1 }} />
              Installazione Manuale
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CodeBlock>
{`# Installare Node.js 18+
curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash -
sudo apt-get install -y nodejs

# Installare PostgreSQL
sudo apt-get install -y postgresql postgresql-contrib

# Creare database
sudo -u postgres createdb edge_gateway
sudo -u postgres psql -c "CREATE USER edge WITH PASSWORD 'edge_password';"
sudo -u postgres psql -c "GRANT ALL PRIVILEGES ON DATABASE edge_gateway TO edge;"

# Installare dipendenze
cd edge-gateway
npm install

# Inizializzare database
npm run init-db`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        <Accordion expanded={expanded === 'panel3'} onChange={handleAccordionChange('panel3')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="3" color="primary" size="small" sx={{ mr: 1 }} />
              Avvio come Servizio Systemd
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Creare file <code>/etc/systemd/system/edge-gateway.service</code>:
            </Typography>
            <CodeBlock>
{`[Unit]
Description=Industria 4.0 Edge Gateway
After=network.target postgresql.service

[Service]
Type=simple
User=gateway
WorkingDirectory=/opt/edge-gateway
ExecStart=/usr/bin/node src/index.js
Restart=always
RestartSec=10
Environment=NODE_ENV=production

[Install]
WantedBy=multi-user.target`}
            </CodeBlock>
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Abilitare e avviare:
            </Typography>
            <CodeBlock>
{`sudo systemctl daemon-reload
sudo systemctl enable edge-gateway
sudo systemctl start edge-gateway
sudo systemctl status edge-gateway`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>
      </TabPanel>

      {/* TAB 2: Configurazione */}
      <TabPanel value={tabValue} index={2}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              File Environment (.env)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creare il file <code>.env</code> nella cartella <code>edge-gateway/</code>:
            </Typography>
            <CodeBlock>
{`# CLOUD API CONFIGURATION
CLOUD_API_URL=http://backend:5000/api/edge
EDGE_GATEWAY_NAME=Gateway Officina 1

# Credenziali (ottenute dopo registrazione sul cloud)
EDGE_GATEWAY_ID=
EDGE_API_KEY=

# POLLING CONFIGURATION
POLLING_INTERVAL_SECONDS=30
SYNC_BATCH_SIZE=100

# DATABASE CONFIGURATION
DATABASE_URL=postgresql://edge:edge_password@localhost:5432/edge_gateway

# LOGGING
LOG_LEVEL=info
LOG_FILE=/var/log/edge-gateway.log

# OPC UA CONFIGURATION
OPCUA_RECONNECT_DELAY=5000
OPCUA_CONNECTION_TIMEOUT=30000`}
            </CodeBlock>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <NetworkCheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Configurazione Macchine
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Parametri per ogni macchina OPC UA:
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Parametro</TableCell>
                    <TableCell>Descrizione</TableCell>
                    <TableCell>Esempio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><code>opcua_endpoint_url</code></TableCell>
                    <TableCell>URL del server OPC UA</TableCell>
                    <TableCell><code>opc.tcp://192.168.1.100:4840/UA/Machine</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>opcua_security_mode</code></TableCell>
                    <TableCell>Modalita sicurezza</TableCell>
                    <TableCell><code>None</code>, <code>Sign</code>, <code>SignAndEncrypt</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>opcua_security_policy</code></TableCell>
                    <TableCell>Policy sicurezza</TableCell>
                    <TableCell><code>None</code>, <code>Basic256Sha256</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>opcua_username</code></TableCell>
                    <TableCell>Username (opzionale)</TableCell>
                    <TableCell><code>opcuser</code></TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>opcua_password</code></TableCell>
                    <TableCell>Password (opzionale)</TableCell>
                    <TableCell><code>***</code></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Nodi OPC UA Monitorati
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Il gateway monitora di default questi nodi (namespace <code>ns=1</code>):
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Node ID</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Descrizione</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><code>ns=1;s=MachineStatus</code></TableCell>
                    <TableCell>MachineStatus</TableCell>
                    <TableCell>Stato corrente macchina</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>ns=1;s=CurrentSpeed</code></TableCell>
                    <TableCell>CurrentSpeed</TableCell>
                    <TableCell>Velocita produzione</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>ns=1;s=ProductionCount</code></TableCell>
                    <TableCell>ProductionCount</TableCell>
                    <TableCell>Contatore pezzi</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>ns=1;s=ErrorCode</code></TableCell>
                    <TableCell>ErrorCode</TableCell>
                    <TableCell>Codice errore</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><code>ns=1;s=Temperature</code></TableCell>
                    <TableCell>Temperature</TableCell>
                    <TableCell>Temperatura macchina</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* TAB 3: Sicurezza */}
      <TabPanel value={tabValue} index={3}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Livelli di Sicurezza OPC UA
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Security Mode</TableCell>
                    <TableCell>Security Policy</TableCell>
                    <TableCell>Uso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><Chip label="None" size="small" /></TableCell>
                    <TableCell>None</TableCell>
                    <TableCell>Solo sviluppo/test</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="Sign" size="small" color="warning" /></TableCell>
                    <TableCell>Basic256Sha256</TableCell>
                    <TableCell>Firma messaggi</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><Chip label="SignAndEncrypt" size="small" color="success" /></TableCell>
                    <TableCell>Basic256Sha256</TableCell>
                    <TableCell>Produzione (consigliato)</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <FolderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Struttura PKI (Certificati)
            </Typography>
            <CodeBlock>
{`data/opcua-pki/
├── own/
│   ├── certs/          # Certificato client gateway
│   └── private/        # Chiave privata gateway
├── trusted/
│   └── certs/          # Certificati macchine trusted
├── rejected/
│   └── certs/          # Certificati rifiutati
└── issuers/
    └── certs/          # Certificati CA`}
            </CodeBlock>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Generare Certificato Gateway
            </Typography>
            <CodeBlock>
{`# Creare directory PKI
mkdir -p data/opcua-pki/{own/certs,own/private,trusted/certs,rejected/certs}

# Generare certificato self-signed
openssl req -x509 -newkey rsa:2048 \\
  -keyout data/opcua-pki/own/private/gateway_key.pem \\
  -out data/opcua-pki/own/certs/gateway_cert.pem \\
  -days 365 -nodes \\
  -subj "/CN=Industria4Gateway/O=MyCompany/L=Milan/C=IT"

# Impostare permessi
chmod 600 data/opcua-pki/own/private/gateway_key.pem`}
            </CodeBlock>
          </CardContent>
        </Card>
      </TabPanel>

      {/* TAB 4: Troubleshooting */}
      <TabPanel value={tabValue} index={4}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="Errore" color="error" size="small" sx={{ mr: 1 }} />
              Connessione Rifiutata (ECONNREFUSED)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: connect ECONNREFUSED 192.168.1.100:4840
            </Alert>
            <Typography variant="subtitle2">Soluzioni:</Typography>
            <CodeBlock>
{`# 1. Verificare raggiungibilita
ping 192.168.1.100

# 2. Verificare porta
nc -zv 192.168.1.100 4840

# 3. Verificare firewall locale
sudo ufw status

# 4. Verificare che il server OPC UA sia attivo sulla macchina`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="Errore" color="error" size="small" sx={{ mr: 1 }} />
              Timeout Connessione
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: Connection timeout
            </Alert>
            <Typography variant="subtitle2">Soluzioni:</Typography>
            <CodeBlock>
{`# 1. Aumentare timeout in .env
OPCUA_CONNECTION_TIMEOUT=60000

# 2. Verificare latenza rete
ping -c 10 192.168.1.100

# 3. Riavviare gateway
docker-compose restart edge-gateway`}
            </CodeBlock>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="Errore" color="error" size="small" sx={{ mr: 1 }} />
              Certificato Non Valido (BadSecurityChecksFailed)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: BadSecurityChecksFailed
            </Alert>
            <Typography variant="subtitle2">Soluzioni:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Verificare che il certificato gateway sia trusted sulla macchina" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Verificare che il certificato macchina sia in trusted/certs/" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Provare con Security Mode: None per test" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              <Chip label="Errore" color="error" size="small" sx={{ mr: 1 }} />
              Node ID Non Trovato (BadNodeIdUnknown)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="error" sx={{ mb: 2 }}>
              Error: BadNodeIdUnknown
            </Alert>
            <Typography variant="subtitle2">Soluzioni:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="1. Verificare Node IDs corretti usando UaExpert" />
              </ListItem>
              <ListItem>
                <ListItemText primary="2. Verificare namespace index (ns=1 potrebbe essere ns=2)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="3. Usare script di browse per scoprire nodi disponibili" />
              </ListItem>
            </List>
          </AccordionDetails>
        </Accordion>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <PlayArrowIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Comandi Debug Utili
            </Typography>
            <CodeBlock>
{`# Abilitare log dettagliati
LOG_LEVEL=debug

# Visualizzare log gateway
docker-compose logs -f edge-gateway | grep -i opcua

# Log in tempo reale
tail -f /var/log/edge-gateway.log

# Test connessione OPC UA
nc -zv 192.168.1.100 4840`}
            </CodeBlock>
          </CardContent>
        </Card>

        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <BuildIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Tools Utili
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tool</TableCell>
                    <TableCell>Descrizione</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><strong>UaExpert</strong></TableCell>
                    <TableCell>Client OPC UA grafico per esplorare address space</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Prosys OPC UA Browser</strong></TableCell>
                    <TableCell>Browser address space gratuito</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>opcua-commander</strong></TableCell>
                    <TableCell>CLI per test OPC UA</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><strong>Wireshark</strong></TableCell>
                    <TableCell>Analisi traffico OPC UA (dissector integrato)</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default GatewayGuide;
